<template>
  <teleport
    to="body"
    :disabled="!isMounted"
  >
    <transition name="am3-dropdown-fade">
      <div
        v-if="modelValue && isMounted"
        :id="id"
        ref="dropdownRef"
        :data-test-id="dataTestId"
        class="am3-dropdown-panel border shadow-2xl min-w-[150px] fixed z-[60] bg-surface-light border-surface-dark rounded-md overflow-y-auto text-sm [&_p]:min-h-10 [&_p]:flex [&_p]:flex-row [&_p]:items-center [&_li]:min-h-10 [&_li]:flex [&_li]:flex-row [&_li]:items-center "
        :class="[resolvedPosition, fullHeight ? 'max-h-[80vh]' : 'max-h-[400px] has-[.am3-date-range-filter]:max-h-[500px]', fitContent ? '' : 'max-w-[300px]']"
        :style="dropdownStyle"
      >
        <slot />
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import '@/components/am3-generic/core/theme.css'

export type DropdownPosition = 'top-right' | 'top' | 'top-left' | 'left' | 'bottom-left' | 'bottom' | 'bottom-right' | 'right' | 'auto'

/**
 * AM3DropdownPanel component
 *
 * A flexible dropdown panel that can be attached to any element with an ID
 */
export default defineComponent({
  name: 'AM3DropdownPanel',

  props: {
    // v-model for visibility
    modelValue: {
      type: Boolean,
      default: false,
    },

    // Target element ID
    target: {
      type: String,
      required: true,
    },

    // Position of the dropdown relative to the target
    position: {
      type: String as PropType<DropdownPosition>,
      default: 'auto',
      validator: (value: string) => {
        return ['top-right', 'top', 'top-left', 'left', 'bottom-left', 'bottom', 'bottom-right', 'right', 'auto'].includes(value)
      },
    },

    // Whether the dropdown should stretch to match the width of the target
    stretch: {
      type: Boolean,
      default: false,
    },

    // Whether the dropdown's width should use fit to the content
    fitContent: {
      type: Boolean,
      default: false,
    },

    // Whether the dropdown should use maximum available height
    fullHeight: {
      type: Boolean,
      default: false,
    },

    id: {
      type: String,
      default: null,
    },
    dataTestId: {
      type: String,
      default: 'dropdown-panel',
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const dropdownRef = ref<HTMLElement | null>(null)
    const resizeObserver = ref<ResizeObserver | null>(null)
    const isMounted = ref(false)

    // Timestamp of when the dropdown was last opened, used to enforce a 200ms
    // cooldown so scroll/click events cannot close it immediately after opening.
    let openedAt = 0
    const isOpenedTooRecently = () => Date.now() - openedAt < 200

    const targetRect = ref<DOMRect | null>(null)
    const windowWidth = ref(window.innerWidth)
    const windowHeight = ref(window.innerHeight)
    const dropdownStyle = ref({})
    // The position actually used to render the dropdown. Starts from
    // `computedPosition` but may be flipped at measure-time so the dropdown
    // never overlaps its target when there isn't enough room on the preferred side.
    const resolvedPosition = ref<DropdownPosition>('bottom')

    // Calculate the position of the dropdown
    const computedPosition = computed(() => {
      if (props.position !== 'auto') {
        return props.position
      }

      if (!targetRect.value) {
        return 'bottom'
      }

      const rect = targetRect.value
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      // Determine if the target is in the top or bottom half of the screen
      const isTop = centerY < windowHeight.value / 2
      // Determine if the target is in the left or right half of the screen
      const isLeft = centerX < windowWidth.value / 2

      if (isTop) {
        if (isLeft) {
          return 'bottom-right'
        }
        else {
          return 'bottom-left'
        }
      }
      else {
        if (isLeft) {
          return 'top-right'
        }
        else {
          return 'top-left'
        }
      }
    })

    // Update the position of the dropdown
    const updatePosition = () => {
      if (!props.modelValue) return

      const targetElement = document.getElementById(props.target)
      if (!targetElement) {
        console.error(`Target element with ID "${props.target}" not found`)
        return
      }

      targetRect.value = targetElement.getBoundingClientRect()

      if (!dropdownRef.value || !targetRect.value) return

      const rect = targetRect.value
      const dropdown = dropdownRef.value

      let top = 0
      let left = 0
      const width = props.stretch ? `${rect.width}px` : 'auto'
      const OFFSET = 4 // 4px offset from the source

      dropdownStyle.value = {
        minWidth: width,
      }

      nextTick(() => {
        const dropdownRect = dropdown.getBoundingClientRect()
        // Minimum gap to keep between the dropdown and the viewport edge.
        const MARGIN = 8

        let position = computedPosition.value

        // For vertical placements, work out how much room there is on each side
        // of the target. Prefer keeping the dropdown on its requested side and let
        // it shrink down to MIN_HEIGHT before flipping to the opposite side. The
        // available height is published to the slotted content via a CSS variable
        // so a single inner scroll region can size itself — we deliberately do NOT
        // cap the panel's own height here, otherwise the panel's scroll and the
        // content's scroll would nest and produce two scrollbars.
        const MIN_HEIGHT = 220
        const spaceBelow = windowHeight.value - rect.bottom - OFFSET - MARGIN
        const spaceAbove = rect.top - OFFSET - MARGIN
        const isBottom = position.startsWith('bottom')
        const isTop = position.startsWith('top')

        let availableHeight: number | null = null

        if (isBottom || isTop) {
          let placeBelow = isBottom

          // Only flip when the preferred side is too cramped to be usable AND the
          // opposite side genuinely has more room; otherwise stay put and shrink.
          if (isBottom && spaceBelow < MIN_HEIGHT && spaceAbove > spaceBelow) {
            placeBelow = false
            position = position.replace('bottom', 'top') as DropdownPosition
          }
          else if (isTop && spaceAbove < MIN_HEIGHT && spaceBelow > spaceAbove) {
            placeBelow = true
            position = position.replace('top', 'bottom') as DropdownPosition
          }

          availableHeight = Math.max(placeBelow ? spaceBelow : spaceAbove, 0)
        }

        resolvedPosition.value = position

        // `dropdownRect.height` already reflects any size the content took after
        // consuming the available-height variable, so it anchors top-aligned
        // placements to the target's top edge correctly.
        switch (position) {
          case 'top-right':
            top = rect.top - dropdownRect.height - OFFSET
            left = rect.left
            break
          case 'top':
            top = rect.top - dropdownRect.height - OFFSET
            left = rect.left + (rect.width - dropdownRect.width) / 2
            break
          case 'top-left':
            top = rect.top - dropdownRect.height - OFFSET
            left = rect.right - dropdownRect.width
            break
          case 'left':
            top = rect.top + (rect.height - dropdownRect.height) / 2
            left = rect.left - dropdownRect.width - OFFSET
            break
          case 'bottom-left':
            top = rect.bottom + OFFSET
            left = rect.right - dropdownRect.width
            break
          case 'bottom':
            top = rect.bottom + OFFSET
            left = rect.left + (rect.width - dropdownRect.width) / 2
            break
          case 'bottom-right':
            top = rect.bottom + OFFSET
            left = rect.left
            break
          case 'right':
            top = rect.top + (rect.height - dropdownRect.height) / 2
            left = rect.right + OFFSET
            break
        }

        // Clamp horizontally to stay within the viewport. Vertical fit is handled
        // by the flip + available-height logic above, so we no longer push the
        // dropdown back over its target.
        if (left < 0) left = 0
        if (left + dropdownRect.width > windowWidth.value) {
          left = windowWidth.value - dropdownRect.width
        }
        if (top < 0) top = 0

        dropdownStyle.value = {
          top: `${top}px`,
          left: `${left}px`,
          minWidth: width,
          ...(availableHeight != null ? { '--am3-dropdown-content-max-height': `${availableHeight}px` } : {}),
        }
      })
    }

    // Handle window resize
    const handleResize = () => {
      windowWidth.value = window.innerWidth
      windowHeight.value = window.innerHeight
      updatePosition()
    }

    // Handle scroll event
    const handleScroll = (event: Event) => {
      if (isOpenedTooRecently()) return
      if (props.modelValue && dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
        // Don't close if the scroll originated inside a nested teleported dropdown
        // panel (e.g. a calendar popover opened from within this dropdown's content).
        // Since teleported panels are siblings in the DOM rather than descendants of
        // dropdownRef, and this listener uses capture:true so it sees scroll events
        // from anywhere in the document, without this check any scroll inside a
        // nested panel (including Cypress/browser auto-scrolling an element into
        // view) would incorrectly close this outer panel.
        if ((event.target as Element)?.closest?.('.am3-dropdown-panel')) return
        emit('update:modelValue', false)
      }
    }

    // Keyboard navigation
    const getFocusableItems = (): HTMLElement[] => {
      if (!dropdownRef.value) return []
      return Array.from(
        dropdownRef.value.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]',
        ),
      )
    }

    const handleKeyboardNav = (event: KeyboardEvent) => {
      if (!props.modelValue) return
      const items = getFocusableItems()

      if (!items.length && event.key !== 'Escape') return

      const currentIndex = items.indexOf(document.activeElement as HTMLElement)

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          items[currentIndex < items.length - 1 ? currentIndex + 1 : 0]?.focus()
          break
        case 'ArrowUp':
          event.preventDefault()
          items[currentIndex > 0 ? currentIndex - 1 : items.length - 1]?.focus()
          break
        case 'Escape':
          event.preventDefault()
          emit('update:modelValue', false)
          break
        case 'Enter':
          if (currentIndex >= 0) {
            event.preventDefault()
            items[currentIndex]?.click()
          }
          break
      }
    }

    // Handle click outside
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpenedTooRecently()) return
      if (props.modelValue && dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
        const targetElement = document.getElementById(props.target)
        if (targetElement && !targetElement.contains(event.target as Node)) {
          // Don't close if the click landed inside a nested teleported dropdown panel
          if ((event.target as Element).closest?.('.am3-dropdown-panel')) return
          emit('update:modelValue', false)
        }
      }
    }

    // Watch for changes in the modelValue prop
    watch(() => props.modelValue, (newValue) => {
      if (newValue) {
        openedAt = Date.now()
        document.addEventListener('keydown', handleKeyboardNav)
        nextTick(() => {
          updatePosition()
          // Initialize resize observer when dropdown is shown
          if (dropdownRef.value) {
            resizeObserver.value = new ResizeObserver(() => {
              updatePosition()
            })
            resizeObserver.value.observe(dropdownRef.value)
          }
          // Auto-focus first item
          getFocusableItems()[0]?.focus()
        })
      }
      else {
        document.removeEventListener('keydown', handleKeyboardNav)
        // Cleanup resize observer when dropdown is hidden
        if (resizeObserver.value) {
          resizeObserver.value.disconnect()
          resizeObserver.value = null
        }
      }
    })

    // Watch for changes in the target prop
    watch(() => props.target, () => {
      if (props.modelValue) {
        nextTick(() => {
          updatePosition()
        })
      }
    })

    // Lifecycle hooks
    onMounted(() => {
      isMounted.value = true
      window.addEventListener('resize', handleResize)
      window.addEventListener('scroll', handleScroll, true)
      document.addEventListener('click', handleClickOutside)

      // If modelValue is already true on mount (e.g., keepDropdownOpen prop),
      // the modelValue watcher won't fire (no change detected), so we need to
      // calculate the initial position manually.
      if (props.modelValue) {
        openedAt = Date.now()
        nextTick(() => {
          updatePosition()
          if (dropdownRef.value) {
            resizeObserver.value = new ResizeObserver(() => {
              updatePosition()
            })
            resizeObserver.value.observe(dropdownRef.value)
          }
        })
      }
    })

    onBeforeUnmount(() => {
      if (resizeObserver.value) {
        resizeObserver.value.disconnect()
        resizeObserver.value = null
      }
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll, true)
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleKeyboardNav)
    })

    return {
      dropdownRef,
      dropdownStyle,
      computedPosition,
      resolvedPosition,
      isMounted,
      updatePosition,
    }
  },
})
</script>

<style>
@base {
  .am3-dropdown-panel *:not([class*="text-"]) {
    @apply text-sm;
  }
}

/* Transition effects - keep this in CSS for clarity*/
.am3-dropdown-fade-enter-active,
.am3-dropdown-fade-leave-active {
  transition: opacity 75ms ease, transform 75ms ease, box-shadow 75ms ease-in-out;
}

.am3-dropdown-fade-enter-from,
.am3-dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-.5rem);
}

/* Disable pointer events during leave transition to prevent click-through */
.am3-dropdown-fade-leave-active {
  pointer-events: none;
}

/* Ensure pointer events are enabled during enter transition */
.am3-dropdown-fade-enter-active {
  pointer-events: auto;
}

/* Position-specific styles */
.am3-dropdown-panel.top-right,
.am3-dropdown-panel.top,
.am3-dropdown-panel.top-left {
  transform-origin: bottom center;
}

.am3-dropdown-panel.bottom-right,
.am3-dropdown-panel.bottom,
.am3-dropdown-panel.bottom-left {
  transform-origin: top center;
}

.am3-dropdown-panel.left,
.am3-dropdown-panel.right {
  transform-origin: center;
}
</style>
